import { api } from './api';

export interface PublicUser {
  id: string;
  email: string | null;
  name: string | null;
  pictureUrl: string | null;
  emailVerified: boolean;
  countryCode: string | null;
  primaryLocale: string | null;
  timezone: string | null;
  hasPassword: boolean;
  isSiteAdmin?: boolean;
  linked: { google: boolean; microsoft: boolean; github: boolean; apple: boolean };
}

export interface UpdateProfileInput {
  name?: string;
  pictureUrl?: string;
  primaryLocale?: string;
  timezone?: string;
}

const API_BASE = (import.meta.env.VITE_API_URL as string | undefined) || '';

export const authApi = {
  /** Returns the current user when the session cookie is valid, or null. */
  async me(): Promise<PublicUser | null> {
    try {
      const r = await api.get<PublicUser>('/api/auth/me');
      return r.data;
    } catch {
      return null;
    }
  },
  async register(opts: { email: string; name: string; password: string }): Promise<{ user: PublicUser }> {
    const r = await api.post<{ user: PublicUser }>('/api/auth/register', opts);
    return r.data;
  },
  async login(opts: { email: string; password: string }): Promise<{ user: PublicUser }> {
    const r = await api.post<{ user: PublicUser }>('/api/auth/login', opts);
    return r.data;
  },
  async forgotPassword(email: string): Promise<{ ok: boolean }> {
    const r = await api.post<{ ok: boolean }>('/api/auth/forgot-password', { email });
    return r.data;
  },
  async resetPassword(token: string, password: string): Promise<{ ok: boolean }> {
    const r = await api.post<{ ok: boolean }>('/api/auth/reset-password', { token, password });
    return r.data;
  },
  async verifyEmail(token: string): Promise<{ user: PublicUser }> {
    const r = await api.post<{ user: PublicUser }>('/api/auth/verify-email', { token });
    return r.data;
  },
  async resendVerification(email: string): Promise<{ ok: boolean }> {
    const r = await api.post<{ ok: boolean }>('/api/auth/resend-verification', { email });
    return r.data;
  },
  async updateProfile(input: UpdateProfileInput): Promise<PublicUser> {
    const r = await api.patch<PublicUser>('/api/auth/me', input);
    return r.data;
  },
  async uploadAvatar(file: Blob): Promise<PublicUser> {
    const fd = new FormData();
    fd.append('file', file, 'avatar.png');
    const r = await api.post<PublicUser>('/api/auth/me/avatar', fd);
    return r.data;
  },
  async logout(): Promise<{ ok: boolean }> {
    const r = await api.post<{ ok: boolean }>('/api/auth/logout');
    return r.data;
  },
  /** GDPR/APPI right-of-access — triggers a JSON file download of the user's data. */
  async exportMyData(): Promise<void> {
    const r = await api.get('/api/auth/me/export', { responseType: 'blob' });
    const url = URL.createObjectURL(r.data as Blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'knowlez-my-data.json';
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  },
  /** GDPR/APPI right-to-erasure — anonymizes + deactivates the account. */
  async deleteMyAccount(): Promise<{ ok: boolean }> {
    const r = await api.delete<{ ok: boolean }>('/api/auth/me');
    return r.data;
  },
  oauthHref(provider: 'google' | 'apple'): string {
    return `${API_BASE}/auth/${provider}`;
  },
};

/** Pull a clean error message out of an axios error.
 *  Backend returns either `{ message: string }` or `{ message: string[] }`
 *  (validation errors). Caller passes a translated fallback. */
export function readApiError(err: unknown, fallback: string): string {
  const e = err as {
    response?: { data?: { message?: string | string[]; error?: string } };
    message?: string;
  };
  const msg = e?.response?.data?.message;
  if (Array.isArray(msg) && msg.length) return String(msg[0]);
  if (typeof msg === 'string' && msg) return msg;
  if (typeof e?.message === 'string' && e.message) return e.message;
  return fallback;
}
